/**
 * BenchmarkPanel Component - Performance Benchmarks Display
 * Shows user's benchmarks compared to targets
 */
import React, { memo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Award, Target, TrendingUp, TrendingDown, Minus, 
  BarChart3, Star, Zap 
} from "lucide-react";
import { getScoreColor, getScoreBgColor } from "./constants";

const BenchmarkPanel = memo(({ 
  benchmarks = [], 
  loading = false,
  onRefresh 
}) => {
  const getTrendIcon = (trend) => {
    if (trend === "improving") return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === "declining") return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const getTrendLabel = (trend) => {
    if (trend === "improving") return "Improving";
    if (trend === "declining") return "Needs Work";
    return "Stable";
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg bg-white/80">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-600">Loading benchmarks...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (benchmarks.length === 0) {
    return (
      <Card className="border-0 shadow-lg bg-white/80">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
              <BarChart3 className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Benchmarks Yet</h3>
            <p className="text-gray-600 mb-4">
              Complete at least 3 interviews to see your personalized benchmarks.
            </p>
            <Badge variant="outline" className="bg-purple-50 text-purple-700">
              <Star className="w-3 h-3 mr-1" />
              Complete more interviews to unlock
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Stats Card */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-indigo-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Award className="w-5 h-5 text-purple-600" />
            Your Performance Overview
          </CardTitle>
          <CardDescription>
            Based on your last {benchmarks.length} interviews
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benchmarks.slice(0, 4).map((benchmark, idx) => (
              <div 
                key={benchmark.metric || idx}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">
                    {benchmark.metric}
                  </span>
                  {getTrendIcon(benchmark.trend)}
                </div>
                <div className="text-2xl font-bold text-gray-800 mb-1">
                  {benchmark.current_value}
                  {benchmark.unit === "percentage" && "%"}
                  {benchmark.unit === "wpm" && " WPM"}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Target className="w-3 h-3" />
                  Target: {benchmark.target_value}
                  {benchmark.unit === "percentage" && "%"}
                  {benchmark.unit === "wpm" && " WPM"}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Benchmarks */}
      <Card className="border-0 shadow-lg bg-white/80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5 text-blue-600" />
            Detailed Metrics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {benchmarks.map((benchmark, idx) => {
            const percentage = benchmark.target_value > 0 
              ? Math.min((benchmark.current_value / benchmark.target_value) * 100, 100)
              : 0;
            const isOnTarget = benchmark.current_value >= benchmark.target_value;

            return (
              <div key={benchmark.metric || idx} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">
                      {benchmark.metric}
                    </span>
                    {getTrendIcon(benchmark.trend)}
                    <span className="text-xs text-gray-500">
                      {getTrendLabel(benchmark.trend)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${
                      isOnTarget ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {benchmark.current_value}
                      {benchmark.unit === "percentage" && "%"}
                    </span>
                    <span className="text-gray-400">/</span>
                    <span className="text-gray-600">
                      {benchmark.target_value}
                      {benchmark.unit === "percentage" && "%"}
                    </span>
                  </div>
                </div>
                
                <div className="relative">
                  <Progress 
                    value={percentage} 
                    className={`h-2 ${isOnTarget ? 'bg-green-100' : 'bg-orange-100'}`}
                  />
                  {/* Target marker */}
                  <div 
                    className="absolute top-0 w-0.5 h-2 bg-gray-800"
                    style={{ left: '100%' }}
                  />
                </div>

                {benchmark.tips && (
                  <p className="text-xs text-gray-500 mt-1">
                    <Zap className="w-3 h-3 inline mr-1 text-yellow-500" />
                    {benchmark.tips}
                  </p>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
});

BenchmarkPanel.displayName = "BenchmarkPanel";

export default BenchmarkPanel;
